/**
 * Portal data service
 */
import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { NgRedux } from '@angular-redux/store';
import {
  chunk,
  compact,
  concat,
  find,
  forEach,
  get,
  head,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  map,
  merge,
  reduce,
  sortBy,
  toLower,
} from 'lodash';

import { IAppState } from '@app/store';
import {
  Observable,
  observableCombineLatest,
  observableOf,
  observableThrowError,
  rx,
} from '@shared/rx';
import {
  ANY_TOKEN,
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  TIME_FORMATS,
} from '@config/global-constants';
import {
  BackendApiType,
  EHeaderName,
  ENTITY_TYPES,
  IApiListResponse,
  IBaseEntityResponse,
  IBaseEntityType,
  ICompany,
  ICompanyUserType,
  IErrorOverride,
  IEvent,
  IFilterQuery,
  IProcessedEvent,
  IRoleDataType,
  ISite,
  ISiteHierarchy,
} from '@shared/types';
import { environment } from '../../../../environments/environment';
import { HttpErrorHandler } from '@shared/services/base/http-error-handler';
import { CompanyUtilService } from '@shared/services/company';
import { STATE_KEYS } from '../state-service';
import {
  BACKEND_API_TYPE_FIELD_PATH,
  NO_PERMISSION_STATUS,
} from '@shared/constants';
import { XVIHttpUrlEncodingCodec } from '@shared/services/base/xvi-http-url-encoding-codec';
import { HttpErrorService } from '../http-error';
import { lazyInject } from '@app/containers/base/decorators';
import { DmDataService } from '../dm-data/dm-data.service';
import { buildServiceIdentifyContext, toMoment } from '@shared/utils';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';
import {
  batchRequestMap,
  defaultBatchArrayMapAccumulatedPredict,
} from '@app/shared/rx-operators';

@Injectable()
export class XprtDataService {
  public static UTILITY_TYPE: string = 'XprtDataService';
  // See src/app/shared/types/portal/site.type.ts - interface Site for the fields that are retrieved from Portal
  public static readonly SELECT_STORES: string[] = [
    'id',
    'company',
    'name',
    'subscriptions',
    'subscriptions_data',
    'location',
    'access_code',
    'automatic_updates',
    'connected_devices',
    'store_number',
  ];

  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(HttpErrorService) public httpErrorService: HttpErrorService;
  @lazyInject(CompanyUtilService)
  private companyUtilService: CompanyUtilService;
  @lazyInject(XVII18nService)
  private i18nService: XVII18nService;

  public defaultQuery: IFilterQuery = {};
  private url: string = '/me';

  private get baseUrl(): string {
    return environment.xprtBackendURI;
  }

  public constructor(
    private http: HttpClient,
    public ngRedux: NgRedux<IAppState>,
    public injector: Injector,
  ) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public getCurrentUserInfo(): Observable<object> {
    return this.http
      .get(`${this.baseUrl}${this.url}`)
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  /* eslint-disable  */
  protected catchHttpError =
    (errorOverride?: IErrorOverride) =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError((httpError: HttpErrorResponse) => {
          return observableThrowError(
            HttpErrorHandler.composeError(httpError, {
              errorOverride,
              i18nService: this.i18nService,
            }),
          );
        }),
      );

  /**
   * get user info by filter
   * @param query query (only filter works now)
   * @returns user info
   */
  public getUsersInfoByFilter(
    query: IFilterQuery,
    userQuery?: IFilterQuery,
  ): Observable<ICompanyUserType[]> {
    const state = this.ngRedux.getState();
    const companyId = get(state, STATE_KEYS.CURRENT_COMPANY_ID);
    const filter = !query.filter ? '' : `?$filter=${query.filter}`;

    const params: HttpParams = this.createParams({
      ...userQuery,
    });

    return this.http
      .get(`${this.baseUrl}/v1/companies/${companyId}/people${filter}`, {
        params,
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(),
        rx.map((response: IApiListResponse<ICompanyUserType>) =>
          get(response, 'items', []),
        ),
      );
  }

  public fetchCompanies(
    companyIds?: string[],
  ): Observable<{ items: ICompany[]; total: number }> {
    const filter = isEmpty(companyIds)
      ? ''
      : `?$filter=_id eq '${head(companyIds)}'`;

    const headers = new HttpHeaders({
      [EHeaderName.Authorization]: ANY_TOKEN,
    });

    return this.http
      .get(`${this.baseUrl}${this.url}/companies${filter}`, { headers })
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  /**
   * Method which creates HttpParams and returns
   */
  public createParams({
    filter,
    select: selectArg,
    top,
    skip,
    orderby,
    expand,
    effectiveDate,
    include_entities,
    include_nested_type,
    customParams,
    include_sites,
    // eslint-disable-next-line comma-dangle
    ...queryParams
  }: IFilterQuery = {}): HttpParams {
    let params = new HttpParams({ encoder: new XVIHttpUrlEncodingCodec() });

    // add fixed BACKEND_API_TYPE_FIELD_PATH params, will remove by url.interceptor
    params = params.set(BACKEND_API_TYPE_FIELD_PATH, BackendApiType.PORTAL);
    // TODO find better solution for handling params
    if (filter) {
      params = params.set('$filter', filter);
    }
    if (!isEmpty(selectArg)) {
      params = params.set('$select', selectArg.toString());
    }
    if (top) {
      params = params.set('$top', top.toString());
    }
    if (skip) {
      params = params.set('$skip', skip.toString());
    }
    if (orderby) {
      params = params.set('$orderby', orderby.toString());
    }
    forEach(queryParams, (param, key) => {
      params = params.set(key, param.toString());
    });
    if (include_entities) {
      const entityList: string = include_entities
        .map((entityType) => `'${entityType}'`)
        .join(',');
      params = params.set('include_entities', `[${entityList}]`);
    }
    if (include_nested_type) {
      const typeList: string = include_nested_type
        .map((type) => `'${type}'`)
        .join(',');
      params = params.set('include_nested_type', `[${typeList}]`);
    }
    if (customParams) {
      customParams.map(({ key, value }) => {
        // eslint-disable-line
        params = params.set(key, String(value));
      });
    }
    if (expand) {
      params = params.set('$expand', expand.toString());
    }
    if (effectiveDate) {
      params = params.set('effective_date', effectiveDate);
    }
    if (include_sites) {
      params = params.set('include_sites', include_sites.toString());
    }

    return params;
  }

  public getEnvironmentInfo(): Observable<object> {
    return this.http
      .get(`${this.baseUrl}/info`)
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  public getEventDetailById(
    eventId: string,
    userQuery?: IFilterQuery,
  ): Observable<IBaseEntityType> {
    const state = this.ngRedux.getState();
    const companyId = get(state, STATE_KEYS.CURRENT_COMPANY_ID);
    const query: IFilterQuery = merge(
      {},
      this.defaultQuery,
      {
        include_available_sites: 'true',
        available_sites_select: 'name,store_number',
      },
      userQuery,
    );
    const params: HttpParams = this.createParams({
      top: MAX_PAGE_SIZE,
      ...query,
    });

    return this.http
      .get(`${this.baseUrl}/v1/companies/${companyId}/events/${eventId}`, {
        params,
        context: buildServiceIdentifyContext(ENTITY_TYPES.EVENTS),
      })
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  public getEventsByFilter(userQuery: IFilterQuery): Observable<IEvent[]> {
    const state = this.ngRedux.getState();
    const companyId = get(state, STATE_KEYS.CURRENT_COMPANY_ID);
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    const params: HttpParams = this.createParams({
      top: MAX_PAGE_SIZE,
      ...query,
    });

    return this.http
      .get(`${this.baseUrl}/v1/companies/${companyId}/events`, {
        params,
        context: buildServiceIdentifyContext(ENTITY_TYPES.EVENTS),
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(),
        rx.map((response: IApiListResponse<IEvent>) =>
          get(response, 'items', []),
        ),
      );
  }

  /**
   * Bulk Get events by their IDs
   * @param eventIds
   * @param userQuery
   * @param options
   * @returns
   */
  public getEventsByIds(
    eventIds: string[],
    userQuery?: IFilterQuery,
    options: { bulkSize?: number } = {},
  ): Observable<IEvent[]> {
    if (isEmpty(eventIds)) {
      return this.getEventsByFilter(userQuery);
    }

    const bulkSize = get(options, 'bulkSize', DEFAULT_PAGE_SIZE);

    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);

    return batchRequestMap(chunk(eventIds, bulkSize), (chunkEventIds) => {
      const curEventFilter = `_id eq in(${chunkEventIds.join(',')})`;
      const combinedFilter = !isEmpty(query?.filter)
        ? `(${curEventFilter}) and (${query.filter})`
        : curEventFilter;

      return this.getEventsByFilter({
        ...query,
        filter: combinedFilter,
      });
    }).pipe(
      rx.reduce(defaultBatchArrayMapAccumulatedPredict, [] as IEvent[]),
      this.catchHttpErrorAndPickErrorList(),
    );
  }

  public getAllSitesByFilter(
    userQuery: IFilterQuery,
    options: { throwError?: boolean } = {},
  ): Observable<ISite[]> {
    const state = this.ngRedux.getState();
    const companyId = get(state, STATE_KEYS.CURRENT_COMPANY_ID);
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    const params: HttpParams = this.createParams({
      top: MAX_PAGE_SIZE,
      ...query,
    });

    return this.http
      .get(`${this.baseUrl}/v1/companies/${companyId}/sites`, {
        params,
        context: buildServiceIdentifyContext(ENTITY_TYPES.SITE),
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(options.throwError),
        rx.map((response: IApiListResponse<ISite>) =>
          sortBy(get(response, 'items', []), (site) =>
            toLower(get(site, 'name')),
          ),
        ),
      );
  }

  /**
   * Bulk Get sites by their IDs
   * @param siteIds
   * @param userQuery
   * @param options
   * @returns
   */
  public getSitesByIds(
    siteIds: string[],
    userQuery?: IFilterQuery,
    options: { bulkSize?: number } = {},
  ): Observable<ISite[]> {
    if (isEmpty(siteIds)) {
      return this.getAllSitesByFilter(userQuery);
    }

    const bulkSize = get(options, 'bulkSize', DEFAULT_PAGE_SIZE);

    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);

    return batchRequestMap(chunk(siteIds, bulkSize), (chunkSiteIds) => {
      const curSiteFilter = `_id eq in(${chunkSiteIds.join(',')})`;
      const combinedFilter = !isEmpty(query?.filter)
        ? `(${curSiteFilter}) and (${query.filter})`
        : curSiteFilter;

      return this.getAllSitesByFilter({
        ...query,
        filter: combinedFilter,
      });
    }).pipe(
      rx.reduce(defaultBatchArrayMapAccumulatedPredict, [] as ISite[]),
      this.catchHttpErrorAndPickErrorList(),
    );
  }

  public processEventsList(
    events: IEvent[],
    eventTypeMap: object = {},
  ): IProcessedEvent[] {
    return map(events, (event) => {
      const result: IProcessedEvent = {
        ...event,
        date: this.companyUtilService.convertUTCDateWithTimeZone(
          event.event_days[0].start_date_time,
          true,
          TIME_FORMATS.L,
        ),
        date_for_sort:
          toMoment(event.event_days[0].start_date_time).valueOf() * -1,
        status_for_sort: event.status === 'open' ? -1 : 1,
      };
      if (!isEmpty(eventTypeMap)) {
        result.event_type_entity_name = get(
          eventTypeMap,
          event.event_type_entity_id,
          event.event_type_entity_id,
        );
      }

      return result;
    });
  }

  public fetchCompleteEventsList(
    userQuery: IFilterQuery = {},
  ): Observable<IProcessedEvent[]> {
    return observableCombineLatest([
      this.getEventsByFilter(userQuery),
      this.dmDataService.getEventTypes(),
    ]).pipe(
      this.catchHttpErrorAndPickErrorList(),
      rx.map(([events, eventTypes]) => {
        const eventTypeMap = reduce(
          eventTypes,
          (typeMap, eventType) => ({
            ...typeMap,
            [eventType.entity_id]: eventType.name,
          }),
          {},
        );
        const sortedEvents: IProcessedEvent[] = sortBy(
          this.processEventsList(events, eventTypeMap),
          [
            (event: IProcessedEvent): number => event.status_for_sort,
            (event: IProcessedEvent): number => event.date_for_sort,
          ],
        );

        return sortedEvents;
      }),
    );
  }

  public getEventIdsByFilter(userQuery: {
    status?: string[];
    ids: string[];
    defaultFilter?: string[];
  }): Observable<string[]> {
    const statusFilter =
      !userQuery.status || isEmpty(userQuery.status)
        ? null
        : `status eq in(${userQuery.status.join(',')})`;
    const idFilter = `_id eq in(${userQuery.ids.join(',')})`;

    return this.getEventsByFilter({
      filter: compact([
        statusFilter,
        idFilter,
        ...(userQuery.defaultFilter ?? []),
      ]).join(' and '),
    }).pipe(rx.map((events) => map(events, 'id')));
  }

  public fetchStores(query): Observable<object> {
    let params = new HttpParams();
    if (query) {
      if ('limit' in query) params = params.set('limit', query.limit);
      if ('skip' in query) params = params.set('skip', query.skip);
    }

    const fetchUrl = `${this.baseUrl}${this.url}/sites?$select=${XprtDataService.SELECT_STORES}`;

    return this.http
      .get(fetchUrl, {
        params,
        context: buildServiceIdentifyContext(ENTITY_TYPES.SITE),
      })
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  /**
   * Fetch site hierarchies
   *
   * @param {Object} userQuery
   * @returns {Observable<ISiteHierarchy[]>}
   */
  public fetchSiteHierarchies(
    userQuery: IFilterQuery = {},
  ): Observable<ISiteHierarchy[]> {
    const state = this.ngRedux.getState();
    const companyId = get(state, STATE_KEYS.CURRENT_COMPANY_ID);
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    const params: HttpParams = this.createParams({ ...query });

    return this.http
      .get(`${this.baseUrl}/v1/companies/${companyId}/site-hierarchies`, {
        params,
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(),
        rx.map((response: IApiListResponse<ISiteHierarchy>) =>
          get(response, 'items', []),
        ),
      );
  }

  public combineEventIntoEntityResponse(
    res: IBaseEntityResponse,
  ): Observable<IBaseEntityResponse> {
    const eventIds = reduce(
      res.items,
      (prev, curr) => {
        return isNil(curr.event_id) || includes(prev, curr.event_id)
          ? prev
          : concat(prev, curr.event_id);
      },
      [],
    );

    if (isEmpty(eventIds)) {
      return observableOf(res);
    }

    return this.getEventsByFilter({
      filter: `_id eq in(${eventIds.join()})`,
      select: ['id', 'name'],
    }).pipe(
      rx.map((events: IEvent[]) => {
        const eventMap = reduce(
          events,
          (prev, curr) => {
            return {
              ...prev,
              [curr.id]: curr.name,
            };
          },
          {},
        );

        return {
          ...res,
          items: map(res.items, (item: IBaseEntityType) => ({
            ...item,
            event_id: eventMap[item.event_id],
          })),
        };
      }),
    );
  }

  public fetchRoleDataByCompanyId(
    userQuery: IFilterQuery,
  ): Observable<IRoleDataType[]> {
    const state = this.ngRedux.getState();
    const companyId = get(state, STATE_KEYS.CURRENT_COMPANY_ID);
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    const params: HttpParams = this.createParams({
      top: MAX_PAGE_SIZE,
      ...query,
    });

    return this.http
      .get(`${this.baseUrl}/v1/companies/${companyId}/user_roles/active`, {
        params,
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(),
        rx.map((response: IApiListResponse<IRoleDataType>) =>
          get(response, 'items', []),
        ),
      );
  }

  /* eslint-disable */
  private catchHttpErrorAndPickErrorList =
    (throwError: boolean | undefined = false): any =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError(({ error }: HttpErrorResponse) => {
          const errorData = get(error, 'errors');
          const hasNoPermissionError = find(errorData, (errorItem) =>
            isEqual(errorItem.status, NO_PERMISSION_STATUS),
          );
          if (hasNoPermissionError) {
            return this.httpErrorService
              .catchNoPermissionHttpError(errorData)
              .pipe(
                rx.switchMap((newResult) => {
                  return throwError
                    ? observableThrowError(errorData)
                    : observableOf(newResult);
                }),
              );
          }

          return observableThrowError(errorData);
        }),
      );
}
